import { API_VERSION, ParentTokenMessageTypes, TOKEN_BROKER, WebviewFunctions } from 'constants/common';
import { updateSTLTokens } from 'containers/auth/authSlice';
import type { AppDispatch } from 'store/store';
import type { RootState } from 'store/reducers';
import { LocalStorage } from './storage';

const BRIDGE_TIMEOUT_MS = 8000;
const TOKEN_BROKER_KEY = 'tokenBroker';

export type FreshTokenResult =
  | { idToken: string; refreshToken?: string; accessToken?: string }
  | { error: 'SESSION_REVOKED' | 'REFRESH_FAILED' };

type ParentTokenBrokerDeps = {
  getState: () => RootState;
  dispatch: AppDispatch;
};

type GetFreshTokenRequest = {
  type: typeof ParentTokenMessageTypes.GET_FRESH_TOKEN;
  requestId: string;
  staleToken?: string | null;
};

const readQueryParam = (key: string): string | null => {
  const fromSearch = new URLSearchParams(window.location.search).get(key);
  if (fromSearch) return fromSearch;

  const hash = window.location.hash || '';
  const queryIndex = hash.indexOf('?');
  if (queryIndex === -1) return null;
  return new URLSearchParams(hash.slice(queryIndex + 1)).get(key);
};

export const isNativeTokenBroker = (): boolean => {
  if (readQueryParam(TOKEN_BROKER_KEY) === TOKEN_BROKER.NATIVE) {
    if (LocalStorage.get(TOKEN_BROKER_KEY) !== TOKEN_BROKER.NATIVE) {
      LocalStorage.set(TOKEN_BROKER_KEY, TOKEN_BROKER.NATIVE);
    }
    return true;
  }
  return LocalStorage.get(TOKEN_BROKER_KEY) === TOKEN_BROKER.NATIVE;
};

const waitForFlutterBridge = (): Promise<boolean> =>
  new Promise(resolve => {
    if (window.flutter_inappwebview?.callHandler) {
      resolve(true);
      return;
    }

    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('flutterInAppWebViewPlatformReady', onReady);
      resolve(ready);
    };
    const onReady = () => finish(true);

    window.addEventListener('flutterInAppWebViewPlatformReady', onReady);
    window.setTimeout(() => finish(Boolean(window.flutter_inappwebview?.callHandler)), BRIDGE_TIMEOUT_MS);
  });

const parseHandlerResult = (raw: unknown): FreshTokenResult | null => {
  const value = typeof raw === 'string' ? safeParse(raw) : raw;
  if (!value || typeof value !== 'object') return null;

  const result = value as { idToken?: unknown; refreshToken?: unknown; error?: unknown };
  if (typeof result.idToken === 'string' && result.idToken) {
    return {
      idToken: result.idToken,
      ...(typeof result.refreshToken === 'string' && result.refreshToken ? { refreshToken: result.refreshToken } : {})
    };
  }
  if (result.error === 'SESSION_REVOKED' || result.error === 'REFRESH_FAILED') return { error: result.error };
  return null;
};

const safeParse = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

let inFlightNativeRequest: Promise<FreshTokenResult> | null = null;
let inFlightOwnedRequest: Promise<FreshTokenResult> | null = null;

const fetchFreshIdToken = async (staleToken: string | null): Promise<FreshTokenResult> => {
  const ready = await waitForFlutterBridge();
  if (!ready || !window.flutter_inappwebview?.callHandler) {
    return { error: 'REFRESH_FAILED' };
  }

  try {
    const raw = await window.flutter_inappwebview.callHandler(WebviewFunctions.GET_FRESH_TOKEN, { staleToken });
    return parseHandlerResult(raw) ?? { error: 'REFRESH_FAILED' };
  } catch {
    return { error: 'REFRESH_FAILED' };
  }
};

// Concurrent 401s share one bridge call. The app also coalesces refreshes on its side.
export const requestFreshIdToken = (staleToken: string | null): Promise<FreshTokenResult> => {
  if (!inFlightNativeRequest) {
    inFlightNativeRequest = fetchFreshIdToken(staleToken).finally(() => {
      inFlightNativeRequest = null;
    });
  }
  return inFlightNativeRequest;
};

const refreshViaAuthApi = async (): Promise<FreshTokenResult> => {
  const refreshToken = LocalStorage.get<string>('refreshToken');
  if (!refreshToken) return { error: 'REFRESH_FAILED' };

  try {
    const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/${API_VERSION.V1}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });
    if (!response.ok) return { error: 'REFRESH_FAILED' };

    const data = (await response.json()) as {
      idToken?: string;
      accessToken?: string;
      refreshToken?: string;
    };
    if (!data?.idToken) return { error: 'REFRESH_FAILED' };

    return {
      idToken: data.idToken,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken
    };
  } catch {
    return { error: 'REFRESH_FAILED' };
  }
};

const persistOwnedTokens = (
  dispatch: AppDispatch,
  getState: () => RootState,
  fresh: Extract<FreshTokenResult, { idToken: string }>
) => {
  const authState = getState().rootReducer.auth;
  LocalStorage.set('idToken', fresh.idToken);
  if (fresh.accessToken) LocalStorage.set('accessToken', fresh.accessToken);
  if (fresh.refreshToken) LocalStorage.set('refreshToken', fresh.refreshToken);

  dispatch(
    updateSTLTokens({
      idToken: fresh.idToken,
      accessToken: fresh.accessToken ?? authState.accessToken,
      refreshToken: fresh.refreshToken ?? authState.refreshToken
    })
  );
};

/** Parent-owned refresh: Flutter bridge when native, otherwise /auth/refresh. */
export const requestOwnedFreshToken = (
  staleToken: string | null,
  deps: ParentTokenBrokerDeps
): Promise<FreshTokenResult> => {
  if (!inFlightOwnedRequest) {
    inFlightOwnedRequest = (async () => {
      const fresh = isNativeTokenBroker() ? await requestFreshIdToken(staleToken) : await refreshViaAuthApi();
      if ('idToken' in fresh) {
        persistOwnedTokens(deps.dispatch, deps.getState, fresh);
      }
      return fresh;
    })().finally(() => {
      inFlightOwnedRequest = null;
    });
  }
  return inFlightOwnedRequest;
};

const getConfiguredOrigin = (url?: string): string | null => {
  if (!url) return null;
  try {
    return new URL(url.trim()).origin;
  } catch {
    return null;
  }
};

const getTrustedIframeOrigins = (): string[] =>
  [process.env.REACT_APP_ONBOARDING_URL, process.env.REACT_APP_CHECKOUT_ENDPOINT]
    .map(getConfiguredOrigin)
    .filter((origin): origin is string => Boolean(origin));

const isGetFreshTokenRequest = (data: unknown): data is GetFreshTokenRequest => {
  if (!data || typeof data !== 'object') return false;
  const message = data as Partial<GetFreshTokenRequest>;
  return message.type === ParentTokenMessageTypes.GET_FRESH_TOKEN && typeof message.requestId === 'string';
};

/** Listen for onboarding and checkout iframe getFreshToken requests and reply with a fresh idToken. */
export const startParentTokenBrokerListener = (deps: ParentTokenBrokerDeps): (() => void) => {
  const trustedOrigins = getTrustedIframeOrigins();

  const handleMessage = async (event: MessageEvent) => {
    if (trustedOrigins.length > 0 && !trustedOrigins.includes(event.origin)) return;
    if (!isGetFreshTokenRequest(event.data)) return;
    if (!event.source || typeof (event.source as Window).postMessage !== 'function') return;

    const { requestId, staleToken = null } = event.data;
    const target = event.source as Window;
    const replyOrigin = event.origin || trustedOrigins[0] || '*';

    const fresh = await requestOwnedFreshToken(staleToken, deps);
    if ('idToken' in fresh) {
      target.postMessage(
        {
          type: ParentTokenMessageTypes.FRESH_TOKEN,
          requestId,
          idToken: fresh.idToken,
          ...(fresh.refreshToken ? { refreshToken: fresh.refreshToken } : {})
        },
        replyOrigin
      );
      return;
    }

    target.postMessage(
      {
        type: ParentTokenMessageTypes.FRESH_TOKEN,
        requestId,
        error: fresh.error
      },
      replyOrigin
    );
  };

  window.addEventListener('message', handleMessage);
  return () => window.removeEventListener('message', handleMessage);
};

type BuildOnboardingIframeUrlArgs = {
  hashPath?: string;
  params: Record<string, string | number | boolean | null | undefined>;
};

/** Build onboarding iframe URL with isIframe + tokenBroker=parent. */
export const buildOnboardingIframeUrl = ({ hashPath = '/', params }: BuildOnboardingIframeUrlArgs): string => {
  const baseUrl = process.env.REACT_APP_ONBOARDING_URL ?? '';
  const search = new URLSearchParams();
  search.set('isIframe', 'true');
  search.set(TOKEN_BROKER_KEY, TOKEN_BROKER.PARENT);

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });

  const path = hashPath.startsWith('/') ? hashPath : `/${hashPath}`;
  return `${baseUrl}/#${path}?${search.toString()}`;
};

export const buildCheckoutIframeUrl = (
  checkoutUrl: string,
  params: Record<string, string | number | boolean | null | undefined>
): string => {
  const applyParams = (search: URLSearchParams) => {
    search.set('isIframe', 'true');
    search.set(TOKEN_BROKER_KEY, TOKEN_BROKER.PARENT);
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      search.set(key, String(value));
    });
  };

  try {
    const url = new URL(checkoutUrl);
    applyParams(url.searchParams);
    return url.toString();
  } catch {
    const search = new URLSearchParams();
    applyParams(search);
    const joiner = checkoutUrl.includes('?') ? '&' : '?';
    return `${checkoutUrl}${joiner}${search.toString()}`;
  }
};
